// ./src/components/generic/SectionTabs/SectionTabs.tsx
//
// The sibling pages of the section you are currently in, as one horizontally scrollable strip —
// PHONES ONLY.
//
// ⚠⚠ WHY THIS EXISTS, AND WHY IT IS MOBILE-ONLY. A desktop always shows the sidebar, where the
// current section is expanded and every sibling is already on screen; a second copy of the same list
// under the header would be two navigations for one thing. A PHONE has no sidebar. It has four tabs
// and "More", so once you are inside a section — Communications, Program, Operations, Partners — the
// other six or seven pages of that section are reachable only by opening More and drilling back down.
// That is the gap this closes, and it is the gap ROADMAP A2b #51 was about: a destination can exist
// and still be unreachable on the form factor most people use.
//
// ⚠ IT READS THE NAV THAT IS ALREADY ON SCREEN, never the raw sitemap. `DashboardLayout` hands it
// the SAME `navigationItems` the sidebar and tab bar get — scope-filtered, `:eventId` substituted,
// `visibleWhen` applied (a non-ticketed event has no Box Office) and crew-role filtered. So a page a
// host cannot see never appears here, and there is no second filter to keep in step with the first.
// Passing it the raw siteMap would silently re-open every one of those holes.
//
// ⚠ 2–7 mutually exclusive sibling views at one level is the case the tab pattern is FOR, which is
// why this is a visible strip rather than a dropdown: the point is seeing what else the section
// holds without a tap. Above `MAX_TABS` that stops being true — a strip you must scroll twice is
// worse than the More list, which is at least alphabetised and complete — so the strip stands down
// and lets More do its job.
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Group, ScrollArea, Text, UnstyledButton, useMantineTheme } from "@mantine/core";

import { SiteMapItem, preloadRoute } from "@/config";

import { MIN_TABS, sectionTabsFor, activeTabUrl } from "./sectionNav";

export interface SectionTabsProps {
  /** The SAME filtered nav tree the sidebar and bottom bar render. */
  navigationItems: SiteMapItem[];

  /** The live pathname, already carrying a real event id. */
  currentPath: string;

  onNavigate: (path: string) => void;

  /** Shared with the rest of the shell so a tab reads in the host's language. */
  getTranslatedName: (translationKey?: string, fallback?: string) => string;
}

export const SectionTabs: React.FC<SectionTabsProps> = ({
  navigationItems,
  currentPath,
  onNavigate,
  getTranslatedName,
}) => {
  const theme = useMantineTheme();

  // ⚠ Both of these are `sectionNav.ts`, not logic written here — see that file's header. The
  // component's job is what it looks like; which tabs and which one is lit is a rule under test.
  const tabs = useMemo(() => sectionTabsFor(navigationItems, currentPath), [navigationItems, currentPath]);
  const activeUrl = useMemo(() => activeTabUrl(tabs, currentPath), [tabs, currentPath]);

  // ⚠⚠ SCROLL THE LIT TAB INTO VIEW. A seven-tab strip is about twice the width of a phone, so on
  // the later pages of a section — "Sent messages" is the seventh of Communications — the tab that
  // says where you are starts off-screen to the right. What the host sees is a row of tabs with
  // NONE of them highlighted, which reads as "you are nowhere in this section". Caught by rendering
  // the real labels at 375px and looking; no amount of reading the component would have shown it.
  // ⚠ `block: "nearest"` so bringing it into view never scrolls the PAGE vertically as well.
  const activeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [activeUrl]);

  const handleClick = useCallback(
    (url: string) => {
      if (url !== currentPath) {
        onNavigate(url);
      }
    },
    [onNavigate, currentPath],
  );

  if (tabs.length < MIN_TABS) {
    return null;
  }

  return (
    <Box
      hiddenFrom="md"
      style={{ borderBottom: `1px solid ${theme.colors.neutral[2]}` }}
    >
      <ScrollArea type="never" scrollbars="x">
        <Group gap="xs" wrap="nowrap" px="md" py="xs">
          {tabs.map((tab) => {
            const active = tab.url === activeUrl;

            return (
              <UnstyledButton
                key={tab.url}
                ref={active ? activeRef : undefined}
                onClick={() => handleClick(tab.url!)}
                // Phone surface: the press is the earliest signal — see BottomTabBar's note.
                onPointerDown={() => preloadRoute(tab)}
                aria-current={active ? "page" : undefined}
                style={{
                  flexShrink: 0,
                  paddingInline: 14,
                  paddingBlock: 7,
                  borderRadius: theme.radius.xl,
                  backgroundColor: active ? theme.colors.brand[0] : "transparent",
                  border: `1px solid ${active ? theme.colors.brand[3] : theme.colors.neutral[2]}`,
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <Text
                  fw={active ? 600 : 500}
                  c={active ? "brand.7" : "neutral.7"}
                  lh={1.2}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {getTranslatedName(tab.options?.translationKey, tab.name)}
                </Text>
              </UnstyledButton>
            );
          })}
        </Group>
      </ScrollArea>
    </Box>
  );
};
